import React, { useEffect, useId, useState } from 'react';

import { lensTokens } from '~/configs/unifiedAnalyticsTheme';

import BookmarkBorderOutlinedIcon from '@mui/icons-material/BookmarkBorderOutlined';
import LinkOutlinedIcon from '@mui/icons-material/LinkOutlined';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControlLabel,
  LinearProgress,
  Stack,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';

import {
  MAX_PERSONAL_VIEW_NAME_LENGTH,
  type PersonalView,
  type PersonalViewState,
  personalViewFailure,
  useCreatePersonalView,
  useGetPersonalViews,
  useUpdatePersonalView,
} from '~/hooks/apis/reporting/usePersonalViews';
import useCurrentUser from '~/hooks/useCurrentUser';

export interface PersonalViewsProps {
  lensId: string;
  reportVersion: number;
  /** Capture the current view on demand. Throw if its state cannot be safely represented. */
  state: () => PersonalViewState;
  onOpen: (state: PersonalViewState) => void;
  onReset: () => void;
  onCopy: () => Promise<void>;
  /** With onOpenChange the parent owns the dialog and no trigger row is rendered. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

type ViewAction = { type: 'rename' | 'delete' | 'open'; view: PersonalView };
type Feedback = { severity: 'success' | 'error' | 'warning'; message: string };

function validViewState(state: PersonalViewState): boolean {
  return state?.version === 1 && typeof state.search === 'string';
}

function nameError(name: string): string | null {
  if (!name.trim()) return 'Enter a name for this view.';
  if (Array.from(name).some((character) => character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127)) {
    return 'Use a name without control characters.';
  }
  if (name.trim().length > MAX_PERSONAL_VIEW_NAME_LENGTH) {
    return `Use ${MAX_PERSONAL_VIEW_NAME_LENGTH} characters or fewer.`;
  }
  return null;
}

/** Account-private shortcuts shared by generic Lens reports and native reporting pages. */
export default function PersonalViews({
  lensId,
  reportVersion,
  state,
  onOpen,
  onReset,
  onCopy,
  open: controlledOpen,
  onOpenChange,
}: PersonalViewsProps) {
  const theme = useTheme();
  const tokens = lensTokens(theme);
  const dialogId = useId();
  const { isAuthenticated, user } = useCurrentUser();
  const query = useGetPersonalViews(lensId);
  const createView = useCreatePersonalView(lensId);
  const updateView = useUpdatePersonalView(lensId);
  const [ownOpen, setOwnOpen] = useState(false);
  const controlled = Boolean(onOpenChange);
  const open = controlled ? Boolean(controlledOpen) : ownOpen;
  const setOpen = (next: boolean) => (onOpenChange ? onOpenChange(next) : setOwnOpen(next));
  const [name, setName] = useState('');
  const [makeDefault, setMakeDefault] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [action, setAction] = useState<ViewAction | null>(null);
  const [renamed, setRenamed] = useState('');
  const [actionError, setActionError] = useState<string | null>(null);
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [copying, setCopying] = useState(false);
  const signedIn = Boolean(isAuthenticated && user?.email);
  // A successful read confirms that the companion API is available for this account/report.
  const canManage = signedIn && query.isSuccess;
  const busy = createView.isPending || updateView.isPending;
  const readableViews = query.isSuccess ? query.data.views : [];
  const activeViews = readableViews.filter((view) => !view.deletedAt);
  const deletedViews = readableViews.filter((view) => Boolean(view.deletedAt));
  const currentReportVersion = Math.max(query.data?.reportVersion ?? 0, reportVersion);

  useEffect(() => {
    setOpen(false);
    setName('');
    setMakeDefault(false);
    setShowDeleted(false);
    setAction(null);
    setActionError(null);
    setFeedback(null);
  }, [lensId, user?.email]);

  const close = () => {
    if (!busy) {
      setOpen(false);
      setAction(null);
      setFeedback(null);
    }
  };

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (busy || nameError(name) || !canManage) return;
    setFeedback(null);
    let captured: PersonalViewState;
    try {
      captured = state();
      if (!validViewState(captured)) throw new Error('This view cannot be saved. Reset the report and try again.');
    } catch (error) {
      setFeedback({
        severity: 'warning',
        message: personalViewFailure(error, 'This view cannot be saved. Reset the report and try again.'),
      });
      return;
    }
    try {
      await createView.mutateAsync({ name: name.trim(), state: captured, isDefault: makeDefault });
      setName('');
      setMakeDefault(false);
      setFeedback({ severity: 'success', message: 'View saved to your account.' });
    } catch (error) {
      setFeedback({ severity: 'error', message: personalViewFailure(error, 'Couldn’t save this view. Try again.') });
    }
  };

  const openView = (view: PersonalView) => {
    try {
      if (!validViewState(view.state)) {
        throw new Error('This saved view uses an unsupported format. Save a new view from the current report.');
      }
      onOpen(view.state);
      setAction(null);
      setOpen(false);
      setFeedback(null);
    } catch (error) {
      const message = personalViewFailure(error, 'Couldn’t open this view. Try again.');
      if (action) setActionError(message);
      else setFeedback({ severity: 'error', message });
    }
  };

  const selectAction = (next: ViewAction) => {
    setAction(next);
    setActionError(null);
    setRenamed(next.view.name);
  };

  const update = async (
    input: { id: string; name?: string; isDefault?: boolean; deleted?: boolean },
    message: string,
  ) => {
    setFeedback(null);
    setActionError(null);
    try {
      await updateView.mutateAsync(input);
      setAction(null);
      setFeedback({ severity: 'success', message });
    } catch (error) {
      const failure = personalViewFailure(error, 'Couldn’t update this view. Try again.');
      if (action) setActionError(failure);
      else setFeedback({ severity: 'error', message: failure });
    }
  };

  const copy = async () => {
    if (copying) return;
    setCopying(true);
    setFeedback(null);
    try {
      await onCopy();
      setFeedback({ severity: 'success', message: 'Exact view link copied.' });
    } catch (error) {
      setFeedback({
        severity: 'error',
        message: personalViewFailure(error, 'Couldn’t copy the view link. Try again.'),
      });
    } finally {
      setCopying(false);
    }
  };

  const reset = () => {
    try {
      onReset();
      close();
    } catch (error) {
      setFeedback({ severity: 'error', message: personalViewFailure(error, 'Couldn’t reset the view. Try again.') });
    }
  };

  return (
    <>
      {!controlled && (
        <Stack direction="row" useFlexGap flexWrap="wrap" spacing={0.5} alignItems="center">
          <Button
            aria-haspopup="dialog"
            onClick={() => {
              setFeedback(null);
              setOpen(true);
            }}
            size="small"
            startIcon={<BookmarkBorderOutlinedIcon />}
            sx={{ color: tokens.accent }}
            variant="text"
          >
            Saved views
          </Button>
          <Button
            disabled={copying}
            onClick={() => void copy()}
            size="small"
            startIcon={copying ? <CircularProgress color="inherit" size={16} /> : <LinkOutlinedIcon />}
            sx={{ color: tokens.accent }}
            variant="text"
          >
            {copying ? 'Copying…' : 'Copy exact view link'}
          </Button>
          {!open && feedback && (
            <Alert severity={feedback.severity} onClose={() => setFeedback(null)} sx={{ width: '100%' }}>
              {feedback.message}
            </Alert>
          )}
        </Stack>
      )}

      <Dialog aria-labelledby={dialogId} fullWidth maxWidth="sm" onClose={close} open={open}>
        <DialogTitle id={dialogId}>Saved views</DialogTitle>
        <DialogContent>
          <Stack spacing={2.5}>
            <Typography variant="body2" color="text.secondary">
              Save your filters, dates and tabs. These views are private to your account.
            </Typography>
            {!signedIn && <Alert severity="info">Sign in to save and manage your views.</Alert>}
            {feedback && (
              <Alert severity={feedback.severity} onClose={() => setFeedback(null)}>
                {feedback.message}
              </Alert>
            )}
            {query.isError && (
              <Alert
                severity="error"
                action={
                  <Button disabled={query.isFetching} onClick={() => void query.refetch()} size="small">
                    Retry
                  </Button>
                }
              >
                {[404, 501].includes((query.error as { status?: number }).status ?? 0)
                  ? 'Saved views are not available in this environment yet.'
                  : personalViewFailure(query.error, 'Couldn’t load your saved views.')}
              </Alert>
            )}
            {updateView.isPending && <LinearProgress aria-label="Updating saved views" />}

            <Box component="form" onSubmit={(event) => void save(event)}>
              <Stack spacing={1.5}>
                <Typography variant="subtitle2">Save the current view</Typography>
                <TextField
                  disabled={busy || !canManage}
                  fullWidth
                  helperText={
                    name
                      ? (nameError(name) ?? `${name.trim().length}/${MAX_PERSONAL_VIEW_NAME_LENGTH}`)
                      : 'Choose a name you’ll recognise.'
                  }
                  error={Boolean(name && nameError(name))}
                  inputProps={{ maxLength: MAX_PERSONAL_VIEW_NAME_LENGTH }}
                  label="View name"
                  onChange={(event) => setName(event.target.value)}
                  required
                  size="small"
                  value={name}
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={makeDefault}
                      disabled={busy || !canManage}
                      onChange={(event) => setMakeDefault(event.target.checked)}
                      size="small"
                    />
                  }
                  label={<Typography variant="body2">Make this my default view</Typography>}
                />
                <Box>
                  <Button disabled={busy || !canManage || Boolean(nameError(name))} type="submit" variant="contained">
                    {createView.isPending ? 'Saving…' : 'Save current view'}
                  </Button>
                </Box>
              </Stack>
            </Box>

            <Divider />
            <Stack spacing={1.5}>
              <Typography variant="subtitle2">Your views</Typography>
              <Typography variant="body2" color="text.secondary">
                Your default opens when no view is specified in the link.
              </Typography>
              {query.isLoading && (
                <Stack direction="row" spacing={1} alignItems="center" role="status">
                  <CircularProgress color="inherit" size={18} />
                  <Typography variant="body2">Loading your views…</Typography>
                </Stack>
              )}
              {query.isSuccess && activeViews.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  You haven’t saved a view for this report yet.
                </Typography>
              )}
              {activeViews.map((view) => {
                const changed = view.reportChanged || view.reportVersion !== currentReportVersion;
                return (
                  <Box key={view.id} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                    <Stack direction="row" useFlexGap flexWrap="wrap" alignItems="center" spacing={1}>
                      <Typography variant="body2" sx={{ fontWeight: 600, overflowWrap: 'anywhere', minWidth: 0 }}>
                        {view.name}
                      </Typography>
                      {view.isDefault && <Chip label="Your default" size="small" />}
                    </Stack>
                    {changed && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.75 }}>
                        The report has changed since this view was saved. Some choices may no longer apply.
                      </Typography>
                    )}
                    <Stack direction="row" useFlexGap flexWrap="wrap" spacing={0.5} sx={{ mt: 1 }}>
                      <Button
                        disabled={busy}
                        onClick={() => (changed ? selectAction({ type: 'open', view }) : openView(view))}
                        size="small"
                      >
                        Open
                      </Button>
                      <Button disabled={busy} onClick={() => selectAction({ type: 'rename', view })} size="small">
                        Rename
                      </Button>
                      <Button
                        disabled={busy}
                        onClick={() =>
                          void update(
                            { id: view.id, isDefault: !view.isDefault },
                            view.isDefault ? 'Default view removed.' : 'Default view updated.',
                          )
                        }
                        size="small"
                      >
                        {view.isDefault ? 'Remove default' : 'Make default'}
                      </Button>
                      <Button disabled={busy} onClick={() => selectAction({ type: 'delete', view })} size="small">
                        Delete
                      </Button>
                    </Stack>
                  </Box>
                );
              })}
            </Stack>

            {deletedViews.length > 0 && (
              <Stack spacing={1}>
                <Box>
                  <Button aria-expanded={showDeleted} onClick={() => setShowDeleted(!showDeleted)} size="small">
                    {showDeleted ? 'Hide deleted views' : `Deleted views (${deletedViews.length})`}
                  </Button>
                </Box>
                {showDeleted &&
                  deletedViews.map((view) => (
                    <Stack key={view.id} direction="row" spacing={1} alignItems="center" justifyContent="space-between">
                      <Typography variant="body2" sx={{ minWidth: 0, overflowWrap: 'anywhere' }}>
                        {view.name}
                      </Typography>
                      <Stack direction="row" spacing={0.5} sx={{ flexShrink: 0 }}>
                        <Button disabled={busy} onClick={() => selectAction({ type: 'rename', view })} size="small">
                          Rename
                        </Button>
                        <Button
                          disabled={busy}
                          onClick={() => void update({ id: view.id, deleted: false }, 'View restored.')}
                          size="small"
                        >
                          {updateView.isPending && updateView.variables?.id === view.id ? 'Restoring…' : 'Restore'}
                        </Button>
                      </Stack>
                    </Stack>
                  ))}
              </Stack>
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
          <Button disabled={busy} onClick={reset}>
            Reset to published view
          </Button>
          <Button disabled={busy} onClick={close}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        aria-labelledby={`${dialogId}-action`}
        fullWidth
        maxWidth="xs"
        onClose={busy ? undefined : () => setAction(null)}
        open={Boolean(action)}
      >
        <DialogTitle id={`${dialogId}-action`} sx={{ overflowWrap: 'anywhere' }}>
          {action?.type === 'rename'
            ? 'Rename view'
            : action?.type === 'delete'
              ? `Delete “${action.view.name}”?`
              : 'The report has changed'}
        </DialogTitle>
        <DialogContent>
          {actionError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {actionError}
            </Alert>
          )}
          {action?.type === 'rename' && (
            <TextField
              autoFocus
              disabled={busy}
              error={Boolean(nameError(renamed))}
              fullWidth
              helperText={nameError(renamed) ?? `${renamed.trim().length}/${MAX_PERSONAL_VIEW_NAME_LENGTH}`}
              inputProps={{ maxLength: MAX_PERSONAL_VIEW_NAME_LENGTH }}
              label="View name"
              onChange={(event) => setRenamed(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !busy && !nameError(renamed)) {
                  event.preventDefault();
                  void update({ id: action.view.id, name: renamed.trim() }, 'View renamed.');
                }
              }}
              required
              sx={{ mt: 1 }}
              value={renamed}
            />
          )}
          {action?.type === 'delete' && (
            <DialogContentText>
              You can restore this view from Deleted views.{' '}
              {action.view.isDefault && 'It will also be removed as your default.'}
            </DialogContentText>
          )}
          {action?.type === 'open' && (
            <DialogContentText sx={{ overflowWrap: 'anywhere' }}>
              “{action.view.name}” was saved against an earlier report version. Filters, fields or display choices may
              have changed. Review the report after opening it.
            </DialogContentText>
          )}
        </DialogContent>
        <DialogActions>
          <Button disabled={busy} onClick={() => setAction(null)}>
            Cancel
          </Button>
          <Button
            disabled={busy || (action?.type === 'rename' && Boolean(nameError(renamed)))}
            onClick={() => {
              if (action?.type === 'open') openView(action.view);
              if (action?.type === 'rename') void update({ id: action.view.id, name: renamed.trim() }, 'View renamed.');
              if (action?.type === 'delete')
                void update(
                  { id: action.view.id, deleted: true },
                  'View deleted. You can restore it from Deleted views.',
                );
            }}
            variant="contained"
          >
            {busy
              ? 'Saving…'
              : action?.type === 'rename'
                ? 'Save name'
                : action?.type === 'delete'
                  ? 'Delete view'
                  : 'Open anyway'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
